/**
 * Lazy route chunk loaders and post-login JS warmup orchestration.
 *
 * ## Loader map source
 *
 * `ROUTE_CHUNK_LOADERS_BY_ROUTE_ID` is **derived** from `moduleDefinitions` in
 * `appModuleDefinitions.js`. Add or change `loadPage` / `loadMobilePage` there —
 * not in this file.
 *
 * ## Shared preload cache
 *
 * All paths call `preloadRouteChunk(routeId)`:
 * - `Routes.jsx` — `lazyRouteChunk(routeId)` wraps the same promise
 * - `routePreloaders.js` — sidebar hover / focus intent
 * - `AuthStoreHydrator` — post-login sequential queue
 *
 * Failures drop the cache entry so a later hover or navigation can retry.
 *
 * @module routes/routeChunkLoaders
 * @see docs/route-chunk-preload.md
 * @see docs/runtime-preload.md
 */

import { resolveModuleKeyForPath } from "modules/appModuleAccess";
import { getAppModuleConfig } from "modules/appModuleCatalog";
import { moduleDefinitions } from "modules/appModuleDefinitions";
import { getPostLoginChunkPreloadModuleKeys } from "modules/appModulePreload";

/**
 * Dynamic import factories keyed by registry `routing.routeId` / `routing.mobileRouteId`.
 * Derived from `moduleDefinitions` — edit loaders in `appModuleDefinitions.js` only.
 */
export const ROUTE_CHUNK_LOADERS_BY_ROUTE_ID = Object.freeze(
  Object.fromEntries(
    moduleDefinitions.flatMap((definition) => {
      const pairs = [];
      const { routing, loadPage, loadMobilePage } = definition;

      if (routing?.routeId && loadPage) {
        pairs.push([routing.routeId, loadPage]);
      }
      if (routing?.mobileRouteId && loadMobilePage) {
        pairs.push([routing.mobileRouteId, loadMobilePage]);
      }

      return pairs;
    })
  )
);

/** Normalize route variant; unknown values fall back to desktop. */
function normalizeVariant(variant) {
  return variant === "mobile" ? "mobile" : "desktop";
}

/** Resolve `routeId` for a concrete URL (used to skip current page in post-login queue). */
function getRouteIdForPathname(pathname, variant) {
  const moduleKey = resolveModuleKeyForPath(pathname);
  return moduleKey ? resolveRouteIdForModuleKey(moduleKey, variant) : undefined;
}

// /** Dev-only logging — search `[chunk-preload]` to remove after validation. */
// const logChunkPreload = (...args) => {
//   if (process.env.NODE_ENV === "development") console.log("[chunk-preload]", ...args);
// };

function logRouteChunkPreloadError(routeId, error) {
  console.error("Route chunk preload failed", { routeId, error });
}

/**
 * Map a module key + viewport variant to the registry `routeId` used by loaders.
 *
 * Mobile: returns `undefined` when `mobileEnabled` is false; otherwise prefers
 * `mobileRouteId` when set, else reuses desktop `routeId`.
 *
 * @param {string} moduleKey
 * @param {"desktop"|"mobile"} [variant="desktop"]
 * @returns {string|undefined}
 */
export function resolveRouteIdForModuleKey(moduleKey, variant = "desktop") {
  const moduleConfig = getAppModuleConfig(moduleKey);
  if (!moduleConfig?.routing?.routeId) return undefined;

  const routeVariant = normalizeVariant(variant);
  if (routeVariant === "mobile") {
    if (!moduleConfig.routing.mobileEnabled) return undefined;
    return moduleConfig.routing.mobileRouteId || moduleConfig.routing.routeId;
  }

  return moduleConfig.routing.routeId;
}

/**
 * Factory for a route chunk preloader with its own promise cache.
 * Production uses the default export; tests inject mock loaders without
 * mutating `ROUTE_CHUNK_LOADERS_BY_ROUTE_ID`.
 *
 * @param {Record<string, () => Promise<unknown>>} [loaders]
 * @returns {(routeId: string) => Promise<unknown>}
 */
export function createRouteChunkPreloader(loaders = ROUTE_CHUNK_LOADERS_BY_ROUTE_ID) {
  /** routeId → in-flight or resolved dynamic import promise */
  const chunkPreloadPromises = new Map();

  return function preloadChunk(routeId) {
    const loader = loaders[routeId];
    if (!loader) {
      return Promise.reject(new Error(`Missing route chunk loader for routeId: ${routeId}`));
    }

    // Reuse completed or in-flight promise — sidebar hover + post-login queue dedupe here.
    if (chunkPreloadPromises.has(routeId)) {
      // logChunkPreload(`cache hit: ${routeId}`);
      return chunkPreloadPromises.get(routeId);
    }

    // logChunkPreload(`loading: ${routeId}`);

    let preloadPromise;
    try {
      preloadPromise = Promise.resolve(loader());
    } catch (error) {
      preloadPromise = Promise.reject(error);
    }

    preloadPromise = preloadPromise
      .then((result) => {
        // logChunkPreload(`loaded: ${routeId}`);
        return result;
      })
      .catch((error) => {
        // Drop failed entry so a later hover/navigation can retry the import.
        chunkPreloadPromises.delete(routeId);
        throw error;
      });

    chunkPreloadPromises.set(routeId, preloadPromise);
    return preloadPromise;
  };
}

/** Shared production preloader — single cache for Routes, sidebar, and hydrator. */
export const preloadRouteChunk = createRouteChunkPreloader();

/**
 * Build ordered `routeId` list for post-login background warmup.
 *
 * - Source order: registry `postLoginChunkPreloadOrder` via `getPostLoginChunkPreloadModuleKeys`
 * - Skips `currentPathname` route (user is already on or about to land there)
 * - Dedupes route ids when multiple module keys resolve to the same chunk
 *
 * @param {Object} userData
 * @param {Object} [options]
 * @param {"desktop"|"mobile"} [options.variant="desktop"]
 * @param {string} [options.currentPathname]
 * @returns {string[]}
 */
export function getPostLoginChunkPreloadRouteIds(
  userData,
  { variant = "desktop", currentPathname } = {}
) {
  if (!userData) return [];

  const routeVariant = normalizeVariant(variant);
  const landingRouteId = currentPathname
    ? getRouteIdForPathname(currentPathname, routeVariant)
    : undefined;
  const seenRouteIds = new Set();

  return getPostLoginChunkPreloadModuleKeys(userData)
    .map((moduleKey) => resolveRouteIdForModuleKey(moduleKey, routeVariant))
    .filter((routeId) => {
      if (!routeId || routeId === landingRouteId || seenRouteIds.has(routeId)) {
        return false;
      }
      seenRouteIds.add(routeId);
      return true;
    });
}

/** Yield between queue items so warmup does not starve main-thread paint/input. */
function yieldToBrowser() {
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    return new Promise((resolve) => {
      window.requestIdleCallback(resolve, { timeout: 750 });
    });
  }

  return new Promise((resolve) => setTimeout(resolve, 0));
}

/**
 * Sequential, access-filtered post-login chunk warmup.
 *
 * Fire-and-forget: failures are logged per route and the queue continues.
 * Auth, first paint, and navigation never depend on warmup completing.
 *
 * @param {Object} userData
 * @param {Object} [options]
 * @returns {Promise<string[]>} route ids that were queued
 */
export async function preloadPostLoginModuleChunks(
  userData,
  {
    variant = "desktop",
    currentPathname,
    preloadChunk = preloadRouteChunk,
    waitForIdle = yieldToBrowser,
    onError = logRouteChunkPreloadError,
  } = {}
) {
  const routeIds = getPostLoginChunkPreloadRouteIds(userData, {
    variant,
    currentPathname,
  });

  // logChunkPreload("post-login queue start", { variant, currentPathname, routeIds });

  for (const routeId of routeIds) {
    // logChunkPreload(`post-login warming: ${routeId}`);
    try {
      await preloadChunk(routeId);
    } catch (error) {
      onError(routeId, error);
    }

    await waitForIdle();
  }

  // logChunkPreload("post-login queue done", { routeIds });

  return routeIds;
}
